<template>
  <div
    class="rs-input"
    :class="inputClasses"
  >
    <label
      v-if="label"
      :for="id"
    >{{ label }}<sup v-if="required || rules?.required">*</sup></label>
    <span v-if="helpTooltip">
      <RsIcon
        :id="`form-input-helper-tooltip-${id}`"
        icon="question-circle"
        class="tooltip-question-icon"
      />
      <RsTooltip
        :target="`form-input-helper-tooltip-${id}`"
        :title="helpTooltip"
        :position="helpTooltipPosition"
        :delay="{ show: 200 }"
      />
    </span>
    <slot name="tailLabel" />
    <ValidationProvider
      v-slot="{ errors }"
      class="veeValidate"
      tag="div"
      :name="validationName"
      mode="aggressive"
      :rules="rules"
    >
      <div
        v-if="imported"
        class="import-tooltip"
        :class="{ 'import-tooltip--open': importHover }"
      >
        <p>
          This field is managed by <strong>[Data Source]</strong>.<br>Changing
          this will be temporary.
        </p>
      </div>
      <BInputGroup
        :id="id"
        class="input-wrap"
        :prepend="prepend"
        :append="append"
      >
        <BInputGroupPrepend v-if="icon && !imported">
          <span class="input-group-text">
            <RsIcon
              :icon="icon"
              weight="far"
            />
          </span>
        </BInputGroupPrepend>
        <BInputGroupPrepend
          v-if="imported"
          @click="toggleState"
          @mouseover="importHover = true"
          @mouseleave="importHover = false"
        >
          <span class="input-group-text">
            <RsIcon :icon="isUnlocked ? 'lock-open-alt' : 'lock-alt'" />
          </span>
        </BInputGroupPrepend>
        <!-- Ideally we use type='tel' but initially we want to use a custom type='phone' to not
        hijack type='tel' across the app and introduce this change slowly -->
        <Component
          :is="type === 'phone' ? 'RsPhoneInput' : 'BFormInput'"
          :id="id"
          v-model="input"
          :type="type"
          :disabled="disabled"
          :placeholder="placeholder"
          :maxlength="maxlength"
          :step="step"
          :min="min"
          :name="name"
          :autocomplete="autocomplete"
          :autofocus="autofocus"
          :lazy="isLazy"
          :debounce="debounce"
          :is-international="isInternational"
          :readonly="readonly || (imported && isUnlocked == false)"
          :state="(errors[0] || hasCustomErrorState) ? false : valid ? true : null"
          @keypress="$emit('keypress', $event)"
          @keypress.enter="handleEnter"
          @blur="$emit('blur', $event)"
          @focus="$emit('focus', $event)"
          @valid="onPhoneInputValidityChange"
        />
      </BInputGroup>
      <div
        v-if="errors.length || customErrorMessage || errorMessage"
        class="text--error"
      >
        {{ errors[0] || customErrorMessage || errorMessage }}
      </div>
      <div
        v-else-if="customWarningMessage"
        class="warning-message"
      >
        <RsIcon
          class="text--warning fas icon"
          icon="exclamation-triangle"
        />
        {{ customWarningMessage }}
      </div>
    </ValidationProvider>
  </div>
</template>
<script lang="ts">
/* eslint-disable camelcase */
import { defineComponent, PropType } from 'vue';
import { ValidationProvider, extend } from 'vee-validate';
import {
  required,
  email,
  numeric,
  max,
  min,
  max_value,
  min_value,
  digits,
  regex,
  alpha_num,
} from 'vee-validate/dist/rules';
import { methods } from '@/mixins/validation-mixin';
import { isEmbedCode, embedCodeMsg } from '@/shared/helpers/is-embed-code';
import RsPhoneInput from '@/components/_global/RsPhoneInput.vue';
import i18n from '@/i18n';

extend('email', { ...email, message: () => i18n.t('validation.emailInvalid') as string });
extend('required', { ...required, message: () => i18n.t('validation.fieldRequired') as string });
extend('numeric', { ...numeric, message: () => i18n.t('validation.numericInvalid') as string });
extend('max', { ...max, message: (_field: string, params: Record<string, unknown>) => i18n.t('validation.maxLength', { max: params.length }) as string });
extend('min', { ...min, message: (_field: string, params: Record<string, unknown>) => i18n.t('validation.minLength', { min: params.length }) as string });
extend('max_value', { ...max_value, message: (_field: string, params: Record<string, unknown>) => i18n.t('validation.maxValue', { max: params.max }) as string });
extend('min_value', { ...min_value, message: (_field: string, params: Record<string, unknown>) => i18n.t('validation.minValue', { min: params.min }) as string });
extend('digits', { ...digits, message: (_field: string, params: Record<string, unknown>) => i18n.t('validation.digits', { length: params.length }) as string });
extend('regex', { ...regex, message: () => i18n.t('validation.regexInvalid') as string });
extend('url', { validate: methods.isURL, message: () => i18n.t('validation.urlInvalid') as string });
extend('numSpace', {
  validate: (value) => {
    const trimValue = value.replace(/\s/g, '');
    if (trimValue === '') return true;
    if (
      /^[0-9]*$/im.test(
        trimValue,
      ) === false
    ) {
      return false;
    }
    return true;
  },
  message: () => i18n.t('validation.numericOnly') as string,
});
extend('embedCode', {
  validate: (value) => {
    const trimValue = value.replace(/\s/g, '');

    if (trimValue === '') return true;
    return isEmbedCode(trimValue);
  },
  message: () => i18n.t('validation.embedCodeInvalid') as string,
});
const phoneValidate = (value: string) => {
  const trimValue = value.replace(/\s/g, '');
  if (trimValue === '') return true;
  return /^[+]?[0-9]?[-\s.]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im.test(trimValue);
};
extend('phone', {
  validate: phoneValidate,
  message: () => i18n.t('validation.phoneInvalid') as string,
});
extend('phoneAlt', {
  validate: phoneValidate,
  message: () => i18n.t('validation.phoneAltInvalid') as string,
});
extend('phoneIntl', {
  validate: (value: string) => {
    const trimValue = value.replace(/\s/g, '');
    if (trimValue === '') return true;
    return /^(?:\+1\d{10}|1\d{9,10}|[0,2-9]\d{9})$/.test(trimValue);
  },
  message: () => i18n.t('validation.phoneIntlInvalid') as string,
});
extend('alpha_num', {
  ...alpha_num,
  message: () => i18n.t('validation.alphaNumOnly') as string,
});
extend('zipRegex', {
  validate: (zip: string) => {
    const zipRegex = new RegExp(
      /^\s*[0-9]{5}\s*$|^\s*[A-Z][0-9][A-Z] ?[0-9][A-Z][0-9]\s*$/,
      'i',
    );
    return zipRegex.test(String(zip).toLowerCase());
  },
  params: ['zipRegex'],
  message: () => i18n.t('validation.postalInvalid') as string,
});
extend('urlRegex', {
  validate: methods.isURL,
  params: ['urlRegex'],
  message: () => i18n.t('validation.urlRegexInvalid') as string,
});
extend('rent', {
  ...required,
  validate: (value) => Number(value) >= 1,
  message: () => i18n.t('validation.rentMin') as string,
});

type TInputValue = string | number | null | undefined;

export default defineComponent({
  name: 'RsFormInput',
  components: {
    RsPhoneInput,
    ValidationProvider,
  },
  props: {
    rules: {
      type: Object,
      default: () => {},
    },
    id: {
      type: String,
      default: '',
    },
    label: String,
    placeholder: String,
    readonly: {
      type: Boolean,
      default: false,
    },
    listingScoresHeart: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    helpTooltip: {
      type: String,
      default: '',
    },
    helpTooltipPosition: {
      type: String,
      default: 'right',
      validator: (value: string) => ['top', 'right', 'bottom', 'left'].includes(value),
    },
    htmlDecode: {
      type: Boolean,
      default: false,
    },
    icon: String,
    append: String,
    prepend: String,
    customErrorMessage: {
      type: String as () => string,
      required: false,
    },
    customWarningMessage: {
      type: String as () => string,
      required: false,
    },
    lazy: {
      type: Boolean,
      default: true,
    },
    autofocus: {
      type: Boolean,
      default: false,
    },
    /**
     *  Used to create a read-only input with the option to edit for imported fields.
     *
     */
    imported: {
      type: Boolean,
      default: false,
    },
    debounce: {
      type: Number,
      default: 0,
    },

    name: {
      type: String,
      default: 'input',
    },

    /**
     * success | warning | error | error-alt
     *
     */
    variant: {
      type: String,
      validator: (prop?: string) => ['success', 'warning', 'error', 'error-alt'].includes(prop ?? 'success'),
    },
    /**
     * text | number | email | password | search | url | tel | time | range | color
     *
     */
    type: {
      type: String,
      default: 'text',
      required: true,
      validator: (prop?: string) => [
        'text',
        'number',
        'email',
        'password',
        'search',
        'url',
        'tel',
        'time',
        'range',
        'color',
        'phone',
      ].includes(prop ?? 'text'),
    },
    maxlength: [String, Number],
    value: {
      type: [String, Number] as PropType<TInputValue>,
      default: '',
    },
    autocomplete: {
      type: String,
      default: 'off',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    min: Number,
    step: Number,
    errorHighlight: {
      type: Boolean,
      default: false,
    },
    useNameForValidation: {
      type: Boolean,
    },
    /* Phone Number Input Only */
    isInternational: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isUnlocked: false as boolean,
      importHover: false as boolean,
      valid: null,
      types: [
        'text',
        'number',
        'email',
        'password',
        'search',
        'url',
        'tel',
        'time',
        'range',
        'color',
      ],
      input: '',
      errorMessage: '',
    };
  },
  computed: {
    isLazy(): boolean {
      if (this.lazy && this.debounce > 0) {
        return false;
      }

      return this.lazy;
    },

    hasCustomErrorState(): boolean {
      return (!!this.customErrorMessage || this.errorHighlight);
    },

    inputClasses(): Record<string, boolean> {
      return {
        'rs-input--success': this.variant === 'success',
        'rs-input--warning': this.variant === 'warning',
        'rs-input--error': this.variant === 'error',
        'rs-input--error-alt': this.variant === 'error-alt',
        'rs-input--search': this.type === 'search',
        'rs-input--imported': this.imported,
        'rs-input--unlocked': this.isUnlocked,
        'rs-input--compact': !!this.append || !!this.prepend,
      };
    },

    isHtmlDecode(): boolean {
      if (this.$rsGlobals.$isTranslateMode) {
        return true;
      }
      return this.htmlDecode;
    },
    validationName(): string {
      if (this.useNameForValidation && this.name) {
        return this.name;
      }

      return this.label || this.name || 'field';
    },
  },

  watch: {
    value: {
      immediate: true,
      handler() : void {
        this.input = this.isHtmlDecode ? this.$utils.decodeHtml(`${this.value || ''}`, true) : `${this.value || ''}`;
      },
    },

    input(): void {
      this.$emit('input', this.isHtmlDecode ? this.$utils.encodeHtml(this.input) : this.input);
    },
  },
  methods: {
    handleEnter(data: Event): void {
      this.$emit('enter', data);
    },
    toggleState(): void {
      this.isUnlocked = !this.isUnlocked;
    },
    onPhoneInputValidityChange(value: boolean): void {
      this.$emit('valid', value);
      if (value === false && !this.customErrorMessage) { // === false Avoids null values
        this.errorMessage = this.$ts('validation.phoneInputInvalid');
      } else {
        this.errorMessage = '';
      }
    },
  },
});
</script>
<style lang="scss">
@import '@/styles/_variables.scss';

.rs-input {
  margin-bottom: 1rem;

  .iti {
    width: 100%;
  }

  label {
    display: inline-block;
    margin-bottom: 0.5rem;
    font-size: $form-label-font-size;
    font-weight: $form-label-font-weight;
  }
  input {
    padding: $input-padding;
    width: 100%;
    height: 40px;
    background-color: $white;
    line-height: $input-line-height;
    border-radius: $input-radius;
    border: $input-border;
    color: $input-color;
    font-size: $input-font-size;
    box-sizing: border-box;
    &:focus {
      box-shadow: 0 0 0 3px rgba($info, 0.1);
      border-color: $info;
    }
    @include placeholder {
      color: rgba($dark-gray, 0.4);
    }
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  /* Firefox */
  input[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
  }

  @each $name, $color in $status {
    &--#{$name} {
      input {
        border-color: $color;
        color: darken($color, 20%);
      }
    }
  }

  &--error-alt {
    input {
      border-color: $error;
      color: $error;
      background-color: rgba($error, 0.3);
    }
  }

  &--search {
    .input-group-prepend + input {
      border-left-width: 1px;
    }
  }
}
</style>
